import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, EyeOff, ArrowLeft, Upload, ChevronDown, ChevronUp, History, Sparkles, Eye, X, Check, Share2, ArrowUp, ArrowDown, ArrowUpDown, TrendingUp } from "lucide-react";
import BookCard from "./BookCard";
import BookCover from "./BookCover";
import { amazonUrl } from "../data/books";
import { formatAdminDateTime } from "../utils/adminDateTime";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "digsby_admin_token";

const STATUS_LABELS = { draft: "Draft", coming_soon: "Coming Soon", published: "Published" };
const STATUS_COLORS = {
  draft: "bg-slate-200 text-slate-700",
  coming_soon: "bg-[#FBEF14] text-black",
  published: "bg-[#00EE00] text-black",
};

const emptyForm = () => ({
  title: "", shortTitle: "", category: "Story Books", ageRange: "Ages 2–7",
  audience: "Preschool to First Grade", status: "draft", amazon: "", amazonKindle: "",
  tagline: "", description: "", skills: ["", "", "", ""], color: "#0000FF", image: "",
  featured: false, slug: "", seo: { title: "", metaDescription: "", slug: "", imageAlt: "", keywords: "" },
});

function authHeader() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } };
}

export default function CatalogManager({ onAuthError }) {
  const [view, setView] = useState("list"); // list | form | changelog
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [changelog, setChangelog] = useState([]);
  const [shareStats, setShareStats] = useState({});
  const [shareSort, setShareSort] = useState(null); // null | 'desc' | 'asc'

  const toggleShareSort = () =>
    setShareSort((s) => (s === "desc" ? "asc" : s === "asc" ? null : "desc"));

  const displayBooks = shareSort
    ? [...books].sort((a, b) => {
        const ta = shareStats[a.slug]?.total || 0;
        const tb = shareStats[b.slug]?.total || 0;
        return shareSort === "desc" ? tb - ta : ta - tb;
      })
    : books;
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [seoOpen, setSeoOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [sourceText, setSourceText] = useState("");
  const [diffs, setDiffs] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [recheckResults, setRecheckResults] = useState([]);
  const [recheckOpen, setRecheckOpen] = useState(false);

  const handleErr = useCallback((err) => {
    if (err.response?.status === 401) { onAuthError(); return; }
    toast.error(err.response?.data?.detail || "Something went wrong.");
  }, [onAuthError]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, s] = await Promise.all([
        axios.get(`${API}/api/admin/books`, authHeader()),
        axios.get(`${API}/api/categories`, authHeader()),
        axios.get(`${API}/api/admin/share-stats`, authHeader()),
      ]);
      setBooks(b.data); setCategories(c.data); setShareStats(s.data || {});
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  }, [handleErr]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(emptyForm()); setEditingId(null); setSeoOpen(false); setSourceText(""); setDiffs(null); setView("form"); };
  const openEdit = (b) => {
    setForm({
      ...emptyForm(), ...b,
      skills: [0, 1, 2, 3].map((i) => (b.skills && b.skills[i]) || ""),
      amazon: b.amazon || "", amazonKindle: b.amazonKindle || "",
      seo: { title: "", metaDescription: "", slug: "", imageAlt: "", keywords: "",
        ...(b.seo || {}),
        keywords: Array.isArray(b.seo?.keywords) ? b.seo.keywords.join(", ") : (b.seo?.keywords || "") },
    });
    setEditingId(b.id); setSeoOpen(false); setSourceText(""); setDiffs(null); setView("form");
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSkill = (i, v) => setForm((f) => { const s = [...f.skills]; s[i] = v; return { ...f, skills: s }; });
  const setSeo = (k, v) => setForm((f) => ({ ...f, seo: { ...f.seo, [k]: v } }));

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/admin/upload-cover`, fd, {
        headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" },
      });
      set("image", data.url);
      toast.success("Cover uploaded.");
    } catch (err) { handleErr(err); } finally { setUploading(false); }
  };

  const analyzePayload = (src, extraSource = "") => ({
    title: src.title || "", category: src.category || "", ageRange: src.ageRange || "",
    audience: src.audience || "", tagline: src.tagline || "", description: src.description || "",
    skills: (src.skills || []).map((s) => (s || "").trim()).filter(Boolean),
    seo: {
      ...(src.seo || {}),
      keywords: Array.isArray(src.seo?.keywords)
        ? src.seo.keywords
        : ((src.seo?.keywords || "").split(",").map((k) => k.trim()).filter(Boolean)),
    },
    sourceText: extraSource,
  });

  const runAnalyze = async () => {
    if (!form.title.trim()) { toast.error("Add a title first."); return; }
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/analyze`, analyzePayload(form, sourceText), authHeader());
      const acc = {};
      data.diffs.forEach((d) => { acc[d.field] = d.type !== "unchanged"; });
      setDiffs(data.diffs); setAccepted(acc);
    } catch (err) { handleErr(err); } finally { setAnalyzing(false); }
  };

  const reanalyzeRow = async (b) => {
    openEdit(b);
    setRecheckOpen(false);
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/analyze`, analyzePayload(b), authHeader());
      const acc = {};
      data.diffs.forEach((d) => { acc[d.field] = d.type !== "unchanged"; });
      setDiffs(data.diffs); setAccepted(acc);
    } catch (err) { handleErr(err); } finally { setAnalyzing(false); }
  };

  const recheckAll = async () => {
    const targets = books.filter((b) => b.status !== "draft");
    setRechecking(true); setRecheckResults([]); setRecheckOpen(true);
    const results = [];
    for (const b of targets) {
      try {
        const { data } = await axios.post(`${API}/api/admin/analyze`, analyzePayload(b), authHeader());
        const changed = data.diffs.filter((d) => d.type !== "unchanged");
        results.push({
          id: b.id, title: b.shortTitle, changed: changed.length,
          missingSkills: !(b.skills && b.skills.filter(Boolean).length >= 4),
          missingSeo: !(b.seo && b.seo.title),
        });
      } catch (e) {
        results.push({ id: b.id, title: b.shortTitle, error: true });
      }
      setRecheckResults([...results]);
    }
    setRechecking(false);
  };

  const applyDiffs = () => {
    const next = { ...form, seo: { ...form.seo } };
    diffs.forEach((d) => {
      if (!accepted[d.field]) return;
      if (d.field === "tagline") next.tagline = d.after;
      else if (d.field === "description") next.description = d.after;
      else if (d.field === "skills") next.skills = [0, 1, 2, 3].map((i) => (d.after.split("\n")[i] || ""));
      else if (d.field.startsWith("seo.")) next.seo[d.field.split(".")[1]] = d.after;
    });
    setForm(next); setDiffs(null);
    toast.success("Suggestions applied. Review and save.");
  };

  const save = async (publishConfirm) => {
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    // Confirm updating an already-live title
    const existing = editingId ? books.find((b) => b.id === editingId) : null;
    if (existing && existing.status === "published" && form.status === "published" && !publishConfirm) {
      if (!window.confirm("This book is currently live, publishing will update it for all visitors immediately.")) return;
    }
    setSaving(true);
    const payload = {
      ...form,
      skills: form.skills.map((s) => s.trim()).filter(Boolean),
      seo: { ...form.seo, keywords: form.seo.keywords.split(",").map((k) => k.trim()).filter(Boolean) },
    };
    try {
      if (editingId) await axios.put(`${API}/api/admin/books/${editingId}`, payload, authHeader());
      else await axios.post(`${API}/api/admin/books`, payload, authHeader());
      toast.success(editingId ? "Title updated." : "Title created.");
      await load();
      setView("list");
    } catch (err) { handleErr(err); } finally { setSaving(false); }
  };

  const publishAs = (status) => { set("status", status); setTimeout(() => save(true), 0); };

  const unpublish = async (b) => {
    if (!window.confirm(`Unpublish "${b.shortTitle}"? It will no longer be visible on the site.`)) return;
    try {
      await axios.post(`${API}/api/admin/books/${b.id}/unpublish`, {}, authHeader());
      toast.success("Moved to Draft.");
      load();
    } catch (err) { handleErr(err); }
  };

  const openChangelog = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/changelog`, authHeader());
      setChangelog(data); setView("changelog");
    } catch (err) { handleErr(err); }
  };

  const catOptions = Array.from(new Set([...categories, "Workbooks", "Story Books", "For Educators", form.category].filter(Boolean)));
  const inputCls = "w-full min-h-[46px] rounded-xl border-2 border-black/15 px-4 font-medium outline-none focus:border-[#0000FF]";

  // ---------- LIST ----------
  if (view === "list") {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-bold text-3xl">Catalog</h1>
            <p className="text-slate-600">Manage every title shown on the public site.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={recheckAll} data-testid="catalog-recheck-all"
              className="inline-flex items-center gap-2 min-h-[44px] rounded-full border-2 border-[#00A000] text-[#00A000] px-4 font-bold hover:bg-[#00A000] hover:text-white">
              <Sparkles size={16} /> Recheck All
            </button>
            <button onClick={openChangelog} data-testid="catalog-changelog-btn"
              className="inline-flex items-center gap-2 min-h-[44px] rounded-full border-2 border-black/15 px-4 font-bold hover:bg-[#FBEF14]">
              <History size={16} /> Changelog
            </button>
            <button onClick={openAdd} data-testid="catalog-add-btn"
              className="inline-flex items-center gap-2 min-h-[44px] rounded-full bg-[#FF0000] text-white px-5 font-bold hover:bg-[#d60000]">
              <Plus size={18} /> Add New Title
            </button>
          </div>
        </div>

        {loading ? <p className="text-slate-500 py-16 text-center">Loading…</p> : (
          <div className="overflow-x-auto rounded-3xl border-2 border-black/10 bg-white" data-testid="catalog-table">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#FFFBF2] border-b-2 border-black/10">
                <tr>
                  <th className="p-4 font-heading">Cover</th>
                  <th className="p-4 font-heading">Title</th>
                  <th className="p-4 font-heading">Category</th>
                  <th className="p-4 font-heading">Status</th>
                  <th className="p-4 font-heading">
                    <button
                      type="button"
                      onClick={toggleShareSort}
                      data-testid="share-sort-header"
                      className="inline-flex items-center gap-1 font-heading font-bold hover:text-[#0000FF] transition-colors"
                      title="Sort by total shares"
                    >
                      Shares
                      {shareSort === "desc" ? <ArrowDown size={14} /> : shareSort === "asc" ? <ArrowUp size={14} /> : <ArrowUpDown size={14} className="opacity-40" />}
                    </button>
                  </th>
                  <th className="p-4 font-heading">Updated</th>
                  <th className="p-4 font-heading">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayBooks.map((b) => (
                  <tr key={b.id} className="border-b border-black/5 align-middle" data-testid={`catalog-row-${b.slug}`}>
                    <td className="p-3">
                      <div className="w-12 h-16 rounded-lg overflow-hidden bg-[#FFFBF2] border border-black/10">
                        {b.image ? <img src={b.image} alt="" className="w-full h-full object-contain" /> : null}
                      </div>
                    </td>
                    <td className="p-4 font-bold max-w-xs">{b.shortTitle}</td>
                    <td className="p-4 text-slate-600">{b.category}</td>
                    <td className="p-4"><span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_COLORS[b.status] || ""}`}>{STATUS_LABELS[b.status] || b.status}</span></td>
                    <td className="p-4" data-testid={`share-count-${b.slug}`}>
                      {(() => {
                        const st = shareStats[b.slug];
                        const total = st?.total || 0;
                        const recent7 = st?.recent7 || 0;
                        const daily = st?.daily || [0, 0, 0, 0, 0, 0, 0];
                        const max = Math.max(1, ...daily);
                        const breakdown = st?.counts
                          ? Object.entries(st.counts).map(([k, v]) => `${k}: ${v}`).join(", ")
                          : "";
                        return (
                          <div className="flex items-center gap-3">
                            <span
                              title={breakdown || "No shares yet"}
                              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${total ? "bg-[#0000FF]/10 text-[#0000FF]" : "bg-slate-100 text-slate-400"}`}
                            >
                              <Share2 size={12} /> {total}
                            </span>
                            <span className="flex items-end gap-[2px] h-6" title={`Last 7 days: ${daily.join(", ")}`} data-testid={`share-trend-${b.slug}`}>
                              {daily.map((v, i) => (
                                <span
                                  key={i}
                                  className={`w-[4px] rounded-sm ${v > 0 ? "bg-[#00A000]" : "bg-slate-200"}`}
                                  style={{ height: `${Math.max(3, Math.round((v / max) * 24))}px` }}
                                />
                              ))}
                            </span>
                            {recent7 > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-xs font-bold text-[#00A000]" title="Shares in the last 7 days">
                                <TrendingUp size={12} /> +{recent7}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-4 text-slate-500 whitespace-nowrap">{b.updated_at ? formatAdminDateTime(b.updated_at) : "-"}</td>
                    <td className="p-4">
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => openEdit(b)} data-testid={`catalog-edit-${b.slug}`}
                          className="inline-flex items-center gap-1 rounded-full border-2 border-black/15 px-3 py-1.5 text-xs font-bold hover:bg-[#FBEF14]">
                          <Pencil size={13} /> Edit
                        </button>
                        <button onClick={() => reanalyzeRow(b)} data-testid={`catalog-reanalyze-${b.slug}`}
                          className="inline-flex items-center gap-1 rounded-full border-2 border-[#00A000] text-[#00A000] px-3 py-1.5 text-xs font-bold hover:bg-[#00A000] hover:text-white">
                          <Sparkles size={13} /> Re-analyze
                        </button>
                        {b.status !== "draft" && (
                          <button onClick={() => unpublish(b)} data-testid={`catalog-unpublish-${b.slug}`}
                            className="inline-flex items-center gap-1 rounded-full border-2 border-black/15 px-3 py-1.5 text-xs font-bold hover:bg-slate-100">
                            <EyeOff size={13} /> Unpublish
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {recheckOpen && (
          <RecheckModal rechecking={rechecking} results={recheckResults}
            total={books.filter((b) => b.status !== "draft").length}
            onReview={(id) => { const b = books.find((x) => x.id === id); if (b) reanalyzeRow(b); }}
            onClose={() => setRecheckOpen(false)} />
        )}
      </div>
    );
  }

  // ---------- CHANGELOG ----------
  if (view === "changelog") {
    return (
      <div>
        <button onClick={() => setView("list")} className="inline-flex items-center gap-2 font-bold text-slate-600 hover:text-[#0000FF] mb-6"><ArrowLeft size={18} /> Back to Catalog</button>
        <h1 className="font-heading font-bold text-3xl mb-6">Changelog</h1>
        <div className="rounded-3xl border-2 border-black/10 bg-white divide-y divide-black/5" data-testid="changelog-list">
          {changelog.length === 0 ? <p className="p-8 text-center text-slate-500">No changes logged yet.</p> :
            changelog.map((c) => (
              <div key={c.id} className="p-4 flex items-start gap-3">
                <span className="rounded-full bg-[#0000FF] text-white text-xs font-bold px-2 py-1 mt-0.5">{c.action}</span>
                <div>
                  <p className="font-medium">{c.detail}</p>
                  <p className="text-xs text-slate-500">{formatAdminDateTime(c.at)}</p>
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  }

  // ---------- FORM ----------
  return (
    <div className="max-w-3xl">
      <button onClick={() => setView("list")} className="inline-flex items-center gap-2 font-bold text-slate-600 hover:text-[#0000FF] mb-6"><ArrowLeft size={18} /> Back to Catalog</button>
      <h1 className="font-heading font-bold text-3xl mb-6">{editingId ? "Edit Title" : "Add New Title"}</h1>

      <div className="space-y-5 bg-white rounded-3xl border-2 border-black/10 p-6 sm:p-8">
        <Field label="Title *"><input className={inputCls} data-testid="cat-title" value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
        <Field label="Short title (card display)"><input className={inputCls} data-testid="cat-shorttitle" value={form.shortTitle} onChange={(e) => set("shortTitle", e.target.value)} placeholder="Defaults to Title" /></Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Category">
            <select className={inputCls} data-testid="cat-category" value={form.category} onChange={(e) => set("category", e.target.value === "__new" ? "" : e.target.value)}>
              {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="__new">+ Add new category…</option>
            </select>
            {!catOptions.includes(form.category) && (
              <input className={`${inputCls} mt-2`} data-testid="cat-newcategory" placeholder="New category name" value={form.category} onChange={(e) => set("category", e.target.value)} />
            )}
          </Field>
          <Field label="Status">
            <select className={inputCls} data-testid="cat-status" value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="draft">Draft (hidden)</option>
              <option value="coming_soon">Coming Soon</option>
              <option value="published">Published</option>
            </select>
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Age range"><input className={inputCls} data-testid="cat-agerange" value={form.ageRange} onChange={(e) => set("ageRange", e.target.value)} placeholder="Ages 2–7" /></Field>
          <Field label="Audience descriptor"><input className={inputCls} data-testid="cat-audience" value={form.audience} onChange={(e) => set("audience", e.target.value)} /></Field>
        </div>

        <Field label="Amazon link or ASIN (leave blank for Coming Soon)">
          <input className={inputCls} data-testid="cat-amazon" value={form.amazon} onChange={(e) => set("amazon", e.target.value)} placeholder="https://www.amazon.com/dp/XXXXXXXXXX or bare ASIN" />
        </Field>

        <Field label="Short description (Shop card, ~12 words)">
          <input className={inputCls} data-testid="cat-tagline" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} />
        </Field>

        <Field label="Long-form description (detail page)">
          <textarea className={`${inputCls} py-3 resize-y`} rows={6} data-testid="cat-description" value={form.description} onChange={(e) => set("description", e.target.value)} />
        </Field>

        <Field label="What it teaches (4 short entries)">
          <div className="grid sm:grid-cols-2 gap-2">
            {form.skills.map((s, i) => (
              <input key={i} className={inputCls} data-testid={`cat-skill-${i}`} value={s} onChange={(e) => setSkill(i, e.target.value)} placeholder={`Entry ${i + 1}`} />
            ))}
          </div>
        </Field>

        <Field label="Cover image">
          <div className="flex items-center gap-4">
            <div className="w-16 h-20 rounded-lg overflow-hidden bg-[#FFFBF2] border-2 border-black/10 flex-shrink-0">
              {form.image ? <img src={form.image} alt="" className="w-full h-full object-contain" /> : null}
            </div>
            <label className="inline-flex items-center gap-2 rounded-full border-2 border-black/15 px-4 py-2 font-bold cursor-pointer hover:bg-[#FBEF14]">
              <Upload size={16} /> {uploading ? "Uploading…" : "Upload cover"}
              <input type="file" accept="image/*" className="hidden" data-testid="cat-upload" onChange={(e) => uploadCover(e.target.files[0])} />
            </label>
          </div>
        </Field>

        <label className="flex items-center gap-3 font-bold">
          <input type="checkbox" data-testid="cat-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-5 h-5" />
          Feature on the Home page
        </label>

        {/* AI analyze from source */}
        <Field label="Analyze from source text (optional)">
          <textarea className={`${inputCls} py-3 resize-y`} rows={3} data-testid="cat-sourcetext" value={sourceText}
            onChange={(e) => setSourceText(e.target.value)}
            placeholder="Paste manuscript excerpts, back-cover copy, or notes. If blank, AI works from Title, Category, Age range and Audience." />
          <button onClick={runAnalyze} disabled={analyzing} data-testid="cat-analyze"
            className="mt-3 inline-flex items-center gap-2 min-h-[46px] rounded-full bg-[#00A000] text-white px-5 font-bold hover:bg-[#008800] disabled:opacity-60">
            <Sparkles size={16} /> {analyzing ? "Analyzing…" : "Analyze & Preview"}
          </button>
        </Field>

        {/* SEO collapsible */}
        <div className="border-2 border-black/10 rounded-2xl">
          <button onClick={() => setSeoOpen((o) => !o)} data-testid="cat-seo-toggle" className="w-full flex items-center justify-between p-4 font-heading font-bold">
            SEO settings {seoOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {seoOpen && (
            <div className="p-4 pt-0 space-y-4">
              <Field label="SEO title"><input className={inputCls} data-testid="cat-seo-title" value={form.seo.title} onChange={(e) => setSeo("title", e.target.value)} /></Field>
              <Field label="Meta description"><textarea className={`${inputCls} py-3`} rows={2} data-testid="cat-seo-meta" value={form.seo.metaDescription} onChange={(e) => setSeo("metaDescription", e.target.value)} /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="URL slug"><input className={inputCls} data-testid="cat-seo-slug" value={form.seo.slug} onChange={(e) => setSeo("slug", e.target.value)} /></Field>
                <Field label="Image alt text"><input className={inputCls} data-testid="cat-seo-alt" value={form.seo.imageAlt} onChange={(e) => setSeo("imageAlt", e.target.value)} /></Field>
              </div>
              <Field label="Focus keywords (comma-separated)"><input className={inputCls} data-testid="cat-seo-keywords" value={form.seo.keywords} onChange={(e) => setSeo("keywords", e.target.value)} /></Field>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-2">
          <button onClick={() => setPreviewOpen(true)} data-testid="cat-preview"
            className="inline-flex items-center gap-2 min-h-[48px] rounded-full border-2 border-black/15 px-5 font-bold hover:bg-[#FBEF14]"><Eye size={16} /> Preview</button>
          <button onClick={() => publishAs("published")} disabled={saving} data-testid="cat-publish"
            className="inline-flex items-center min-h-[48px] rounded-full bg-[#0000FF] text-white px-6 font-bold hover:bg-[#0000d6] disabled:opacity-60">Publish Now</button>
          <button onClick={() => publishAs("draft")} disabled={saving} data-testid="cat-save-draft"
            className="inline-flex items-center min-h-[48px] rounded-full border-2 border-black px-6 font-bold hover:bg-[#FBEF14] disabled:opacity-60">Save as Draft</button>
          <button onClick={() => save()} disabled={saving} data-testid="cat-save"
            className="inline-flex items-center min-h-[48px] rounded-full bg-black text-white px-6 font-bold hover:bg-black/80 disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      {diffs && (
        <DiffReview diffs={diffs} accepted={accepted} setAccepted={setAccepted}
          onApply={applyDiffs} onClose={() => setDiffs(null)} />
      )}
      {previewOpen && (
        <PreviewModal form={form} onClose={() => setPreviewOpen(false)} />
      )}
    </div>
  );
}

function DiffReview({ diffs, accepted, setAccepted, onApply, onClose }) {
  const TYPE_BADGE = { generated: "bg-[#00EE00] text-black", revised: "bg-[#FBEF14] text-black", unchanged: "bg-slate-200 text-slate-600" };
  const changed = diffs.filter((d) => d.type !== "unchanged");
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="diff-review">
      <div className="bg-white rounded-3xl max-w-3xl w-full my-8 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Sparkles size={20} className="text-[#00A000]" /> Review AI Suggestions</h2>
          <button onClick={onClose} data-testid="diff-close" className="p-2 rounded-full hover:bg-black/5"><X size={22} /></button>
        </div>
        <p className="text-slate-600 mb-5">Nothing is applied until you accept it. {changed.length} suggested change{changed.length === 1 ? "" : "s"}.</p>
        <div className="space-y-4">
          {diffs.map((d) => (
            <div key={d.field} className={`rounded-2xl border-2 p-4 ${d.type === "unchanged" ? "border-black/5 opacity-60" : "border-black/10"}`} data-testid={`diff-${d.field}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="font-heading font-bold">{d.label}</span>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${TYPE_BADGE[d.type]}`}>{d.type}</span>
                  {d.type !== "unchanged" && (
                    <label className="flex items-center gap-1 text-sm font-bold cursor-pointer">
                      <input type="checkbox" data-testid={`diff-accept-${d.field}`} checked={!!accepted[d.field]}
                        onChange={(e) => setAccepted((a) => ({ ...a, [d.field]: e.target.checked }))} className="w-4 h-4" /> Accept
                    </label>
                  )}
                </div>
              </div>
              {d.type !== "unchanged" && (
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-red-50 border border-red-100 p-3 whitespace-pre-line"><span className="block text-xs font-bold text-red-400 mb-1">BEFORE</span>{d.before || <em className="text-slate-400">(empty)</em>}</div>
                  <div className="rounded-xl bg-green-50 border border-green-100 p-3 whitespace-pre-line"><span className="block text-xs font-bold text-green-500 mb-1">AFTER</span>{d.after}</div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onApply} data-testid="diff-apply" className="inline-flex items-center gap-2 min-h-[48px] rounded-full bg-[#00A000] text-white px-6 font-bold hover:bg-[#008800]"><Check size={18} /> Apply Selected</button>
          <button onClick={onClose} className="inline-flex items-center min-h-[48px] rounded-full border-2 border-black/15 px-6 font-bold hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ form, onClose }) {
  const book = {
    ...form,
    shortTitle: form.shortTitle || form.title,
    skills: form.skills.filter((s) => s && s.trim()),
  };
  const comingSoon = !(form.status === "published" && form.amazon);
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="preview-modal">
      <div className="bg-[#FFFBF2] rounded-3xl max-w-5xl w-full my-8 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Eye size={20} /> Live Preview</h2>
          <button onClick={onClose} data-testid="preview-close" className="p-2 rounded-full hover:bg-black/5"><X size={22} /></button>
        </div>

        <h3 className="font-heading font-bold text-lg mb-3">Shop grid card</h3>
        <div className="max-w-xs mb-8"><BookCard book={book} /></div>

        <h3 className="font-heading font-bold text-lg mb-3">Detail page</h3>
        <div className="grid gap-8 lg:grid-cols-2 items-start bg-white rounded-3xl border-2 border-black/10 p-6">
          <div className="aspect-[3/4] max-w-xs mx-auto rounded-2xl overflow-hidden border-4 border-black">
            <BookCover book={book} />
          </div>
          <div>
            <span className="inline-block rounded-full px-4 py-1.5 text-sm font-bold mb-4" style={{ backgroundColor: book.category === "Workbooks" ? "#0000FF" : "#00EE00", color: book.category === "Workbooks" ? "#fff" : "#000" }}>{book.category}</span>
            <h1 className="font-heading font-bold text-3xl leading-tight">{book.shortTitle}</h1>
            <div className="mt-3 flex flex-wrap gap-2">
              {book.ageRange && <span className="rounded-full bg-[#FFFBF2] border-2 border-black/10 px-3 py-1 text-sm font-bold">{book.ageRange}</span>}
              {book.audience && <span className="rounded-full bg-[#FFFBF2] border-2 border-black/10 px-3 py-1 text-sm font-bold">{book.audience}</span>}
            </div>
            <p className="mt-4 text-slate-700 whitespace-pre-line">{book.description}</p>
            {book.skills.length > 0 && (
              <>
                <h4 className="font-heading font-bold text-lg mt-6 mb-3">What it teaches</h4>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {book.skills.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 bg-[#FFFBF2] rounded-xl border-2 border-black/5 p-3 text-sm font-medium">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-[#00EE00] flex items-center justify-center flex-shrink-0"><Check size={13} strokeWidth={3} /></span>{s}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="mt-6">
              {comingSoon ? (
                <span className="inline-flex items-center rounded-full bg-[#FBEF14] border-2 border-black px-6 py-3 font-bold">Coming Soon</span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-[#FF0000] text-white px-6 py-3 font-bold">Buy on Amazon</span>
              )}
              <p className="mt-2 text-sm text-slate-500">{comingSoon ? "This title isn't available yet, check back soon!" : "Opens Amazon in a new tab. Payment, printing, and shipping are handled by Amazon."}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecheckModal({ rechecking, results, total, onReview, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="recheck-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full my-8 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Sparkles size={20} className="text-[#00A000]" /> Catalog Recheck</h2>
          <button onClick={onClose} data-testid="recheck-close" className="p-2 rounded-full hover:bg-black/5"><X size={22} /></button>
        </div>
        <p className="text-slate-600 mb-5">
          {rechecking ? `Analyzing ${results.length} of ${total} titles…` : `Done. Reviewed ${results.length} titles. Nothing is applied until you review each one.`}
        </p>
        <div className="space-y-2">
          {results.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 rounded-2xl border-2 border-black/10 p-4" data-testid={`recheck-row-${r.id}`}>
              <div>
                <p className="font-bold">{r.title}</p>
                <p className="text-sm text-slate-500">
                  {r.error ? "Analysis failed" :
                    <>
                      {r.changed} suggested change{r.changed === 1 ? "" : "s"}
                      {r.missingSkills && <span className="text-[#FF0000] font-bold"> · missing 'what it teaches'</span>}
                      {r.missingSeo && <span className="text-[#FF0000] font-bold"> · missing SEO</span>}
                    </>}
                </p>
              </div>
              {!r.error && (
                <button onClick={() => onReview(r.id)} data-testid={`recheck-review-${r.id}`}
                  className={`rounded-full px-4 py-2 text-sm font-bold ${r.changed > 0 || r.missingSkills || r.missingSeo ? "bg-[#0000FF] text-white hover:bg-[#0000d6]" : "border-2 border-black/15 hover:bg-slate-50"}`}>
                  {r.changed > 0 || r.missingSkills || r.missingSeo ? "Review & Apply" : "Review"}
                </button>
              )}
            </div>
          ))}
          {rechecking && <p className="text-center text-slate-400 py-4">Loading…</p>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-heading font-bold mb-2 text-sm">{label}</label>
      {children}
    </div>
  );
}
